import { Auth } from '@aws-amplify/auth';
import includes from 'lodash/includes';
import axios from 'axios';
import qs from 'qs';

import { networkParams } from 'api/config';
import { convertKeysToCamelCaseDeeply, convertKeysToSnakeCaseDeeply } from 'utils/deepMapKeys';
import { getTokens, setTokens } from 'utils/tokensActions';
import { STORAGE_KEYS } from 'utils/constants/constants';

const { REACT_APP_PLATFORM_API } = process.env;

/**
 * Create axios instance
 * @type {AxiosInstance}
 */
const engineAPI = axios.create({
  ...networkParams,
  paramsSerializer: (params) => qs.stringify(params, { indices: false }),
  transformRequest: [
    (data, headers) => {
      if (headers.withoutAuthorization) {
        delete headers.common.Authorization;
      }

      if (headers.dontConvert) {
        return data;
      }

      return data && convertKeysToSnakeCaseDeeply(data);
    },
    ...axios.defaults.transformRequest,
  ],
  transformResponse: [
    (data, headers) => {
      if (includes(headers['content-disposition'], 'attachment')) {
        return data;
      }

      return convertKeysToCamelCaseDeeply(JSON.parse(data || '{}'));
    },
    ...axios.defaults.transformResponse,
  ],
});

/**
 * Set authorization token for requests
 * @param data
 * @returns {string}
 */

// TODO we can take token from aws directly
export const setAuthorization = (data = {}) => {
  if (!data.token) {
    delete engineAPI.defaults.headers.common.Authorization;
  } else {
    engineAPI.defaults.headers.common.Authorization = `Bearer ${data.token}`;
  }

  return data.token;
};

engineAPI.interceptors.response.use((response) => response, async(error) => {
  const originalRequest = error.config;

  if (error?.response?.status === 401 && !originalRequest._retry) {
    try {
      const {
        idToken: { jwtToken: idToken },
        accessToken: { jwtToken: accessToken },
        refreshToken: { token: refreshToken },
      } = await Auth.currentSession();

      setAuthorization({ token: idToken });
      setTokens({ idToken, accessToken, refreshToken });
      originalRequest._retry = true;
      originalRequest.headers.Authorization = 'Bearer ' + idToken;
      if (typeof originalRequest.data === 'string') {
        originalRequest.transformRequest = axios.defaults.transformRequest;
      }
    } catch (err) {
      const { idToken } = getTokens();

      if (!idToken) {
        window.location.reload();
      }
    }

    return engineAPI(originalRequest);
  }

  return Promise.reject(error);
});

engineAPI.interceptors.request.use((request) => {
  const isPlatformRequest = includes(request.url, REACT_APP_PLATFORM_API);
  const userSelection = localStorage.getItem(STORAGE_KEYS.userSelection);
  const isOriginalUser = request.url.split('?')[1] === 'original_user=true';

  if (userSelection && !isOriginalUser) {
    if (isPlatformRequest) {
      request.headers['X-Autods-User-Id'] = JSON.parse(userSelection).id;
    } else {
      request.headers['X-Autods-Username'] = JSON.parse(userSelection).cognitoUsername;
    }
  }

  return request;
});

export { engineAPI };
